// pages/portals/components/ResumeManager.jsx
// Drop into JobSearchPortal — full resume upload, parse, edit, delete
import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../pages/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://dev.arhat.info';

// ── Theme ─────────────────────────────────────────────────────────
const T = {
  bg:     '#07080c', bg2: '#0c0f18', bg3: '#111520',
  border: 'rgba(255,255,255,.07)', borderH: 'rgba(255,255,255,.13)',
  white:  '#dce6fa', muted: 'rgba(220,230,250,.45)', dim: 'rgba(220,230,250,.22)',
  cyan:   '#00c8f0', blue:  '#4d8fff', green: '#2ecc71',
  red:    '#e74c3c', yellow:'#f1c40f', orange:'#e67e22',
};

function authHeaders() {
  const token = localStorage.getItem('access_token');
  return { Authorization: `Bearer ${token}` };
}

// ── Skill chip ────────────────────────────────────────────────────
function Chip({ label, color = T.cyan, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 600,
      background: `${color}15`, border: `1px solid ${color}30`, color,
      margin: '2px',
    }}>
      {label}
      {onRemove && (
        <button onClick={onRemove} style={{ background: 'none', border: 'none', color, cursor: 'pointer', fontSize: 12, padding: 0, lineHeight: 1 }}>×</button>
      )}
    </span>
  );
}

// ── Upload zone ───────────────────────────────────────────────────
function UploadZone({ onUpload, uploading, count, limit }) {
  const [name,     setName]     = useState('');
  const [file,     setFile]     = useState(null);
  const [drag,     setDrag]     = useState(false);
  const [error,    setError]    = useState('');
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    const allowed = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(f.type)) { setError('Only PDF or DOCX files supported'); return; }
    if (f.size > 5 * 1024 * 1024) { setError('File must be under 5MB'); return; }
    setFile(f);
    setError('');
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async () => {
    if (!file || !name.trim()) { setError('Add a name and select a file'); return; }
    setError('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', name.trim());
    const ok = await onUpload(fd);
    if (ok) { setFile(null); setName(''); }
  };

  if (count >= limit) return (
    <div style={{ padding: '16px', borderRadius: 10, background: `${T.orange}10`, border: `1px solid ${T.orange}30`, fontSize: 13, color: T.orange }}>
      ⚠ You've reached the {limit}-resume limit. Delete a resume to upload a new one.
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <input
          value={name} onChange={e => setName(e.target.value)}
          placeholder="Resume name e.g. Senior PM Resume"
          style={{
            flex: 1, padding: '9px 12px', borderRadius: 8,
            background: T.bg3, border: `1px solid ${T.border}`,
            color: T.white, fontSize: 13, fontFamily: 'inherit', outline: 'none',
          }}
        />
      </div>

      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
        style={{
          border: `2px dashed ${drag ? T.cyan : file ? T.green : T.border}`,
          borderRadius: 10, padding: '24px', textAlign: 'center',
          cursor: 'pointer', transition: 'all .2s',
          background: drag ? `${T.cyan}05` : file ? `${T.green}05` : 'transparent',
        }}
      >
        <div style={{ fontSize: 28, marginBottom: 8 }}>{file ? '📄' : '📁'}</div>
        <div style={{ fontSize: 13, color: file ? T.green : T.muted }}>
          {file ? file.name : 'Click or drag PDF / DOCX here'}
        </div>
        {file && <div style={{ fontSize: 11, color: T.dim, marginTop: 4 }}>{(file.size / 1024).toFixed(0)} KB</div>}
        <input ref={fileRef} type="file" accept=".pdf,.docx" style={{ display: 'none' }} onChange={e => handleFile(e.target.files[0])}/>
      </div>

      {error && <div style={{ fontSize: 12, color: T.red }}>{error}</div>}

      <button
        onClick={handleSubmit}
        disabled={uploading || !file || !name.trim()}
        style={{
          padding: '10px', borderRadius: 8, border: 'none',
          background: `linear-gradient(135deg,${T.cyan},${T.blue})`,
          color: '#07080c', fontSize: 13, fontWeight: 800,
          cursor: uploading ? 'default' : 'pointer', fontFamily: 'inherit',
          opacity: (!file || !name.trim()) ? 0.5 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {uploading ? <><Spinner/> Uploading & Parsing…</> : '⬆ Upload & Parse with AI'}
      </button>
      <div style={{ fontSize: 11, color: T.dim, textAlign: 'center' }}>
        {count}/{limit} resumes used · AI extracts skills, experience, achievements automatically
      </div>
    </div>
  );
}

// ── Edit modal ────────────────────────────────────────────────────
function EditModal({ resume, onSave, onClose }) {
  const [form, setForm] = useState({
    name:             resume.name,
    skills:           [...(resume.skills || [])],
    achievements:     [...(resume.achievements || [])],
    years_experience: resume.years_experience || '',
    career_goal:      resume.career_goal || '',
    summary:          resume.summary || '',
    experience:       JSON.parse(JSON.stringify(resume.experience || [])),
  });
  const [newSkill, setNewSkill] = useState('');
  const [newAchievement, setNewAchievement] = useState('');
  const [saving, setSaving] = useState(false);

  const addSkill = () => {
    const s = newSkill.trim();
    if (s && !form.skills.includes(s)) {
      setForm(f => ({ ...f, skills: [...f.skills, s] }));
      setNewSkill('');
    }
  };

  const addAchievement = () => {
    const a = newAchievement.trim();
    if (a) {
      setForm(f => ({ ...f, achievements: [...f.achievements, a] }));
      setNewAchievement('');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,.7)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 640, maxHeight: '90vh',
        background: T.bg2, border: `1px solid ${T.border}`,
        borderRadius: 14, display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: T.white }}>✏️ Edit Resume</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.muted, fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Name */}
          <Field label="Resume Name">
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle}/>
          </Field>

          {/* Years */}
          <Field label="Years of Experience">
            <input type="number" step="0.5" min="0" max="50" value={form.years_experience} onChange={e => setForm(f => ({ ...f, years_experience: e.target.value }))} style={{ ...inputStyle, width: 120 }}/>
          </Field>

          {/* Summary */}
          <Field label="Professional Summary">
            <textarea value={form.summary} onChange={e => setForm(f => ({ ...f, summary: e.target.value }))} rows={3} style={{ ...inputStyle, resize: 'vertical' }}/>
          </Field>

          {/* Career goal */}
          <Field label="Career Goal">
            <textarea value={form.career_goal} onChange={e => setForm(f => ({ ...f, career_goal: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }}/>
          </Field>

          {/* Skills */}
          <Field label="Skills">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 8 }}>
              {form.skills.map((s, i) => (
                <Chip key={i} label={s} onRemove={() => setForm(f => ({ ...f, skills: f.skills.filter((_, j) => j !== i) }))}/>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={newSkill} onChange={e => setNewSkill(e.target.value)} onKeyDown={e => e.key === 'Enter' && addSkill()} placeholder="Add skill + Enter" style={{ ...inputStyle, flex: 1 }}/>
              <button onClick={addSkill} style={smallBtnStyle}>Add</button>
            </div>
          </Field>

          {/* Achievements */}
          <Field label="Key Achievements">
            {form.achievements.map((a, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input value={a} onChange={e => setForm(f => ({ ...f, achievements: f.achievements.map((x, j) => j === i ? e.target.value : x) }))} style={{ ...inputStyle, flex: 1 }}/>
                <button onClick={() => setForm(f => ({ ...f, achievements: f.achievements.filter((_, j) => j !== i) }))} style={{ ...smallBtnStyle, background: `${T.red}20`, color: T.red }}>✕</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <input value={newAchievement} onChange={e => setNewAchievement(e.target.value)} onKeyDown={e => e.key === 'Enter' && addAchievement()} placeholder="Add achievement + Enter" style={{ ...inputStyle, flex: 1 }}/>
              <button onClick={addAchievement} style={smallBtnStyle}>Add</button>
            </div>
          </Field>

          {/* Experience */}
          <Field label="Work Experience">
            {form.experience.map((exp, i) => (
              <div key={i} style={{ padding: '12px', borderRadius: 8, background: T.bg3, border: `1px solid ${T.border}`, marginBottom: 8 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 3 }}>Company</div>
                    <input value={exp.company || ''} onChange={e => setForm(f => ({ ...f, experience: f.experience.map((x, j) => j === i ? { ...x, company: e.target.value } : x) }))} style={inputStyle}/>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 3 }}>Role</div>
                    <input value={exp.role || ''} onChange={e => setForm(f => ({ ...f, experience: f.experience.map((x, j) => j === i ? { ...x, role: e.target.value } : x) }))} style={inputStyle}/>
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: T.muted, marginBottom: 3 }}>Duration</div>
                    <input value={exp.duration || ''} onChange={e => setForm(f => ({ ...f, experience: f.experience.map((x, j) => j === i ? { ...x, duration: e.target.value } : x) }))} style={inputStyle}/>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: T.muted, marginBottom: 3 }}>Description</div>
                <textarea value={exp.description || ''} onChange={e => setForm(f => ({ ...f, experience: f.experience.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }}/>
                <button onClick={() => setForm(f => ({ ...f, experience: f.experience.filter((_, j) => j !== i) }))} style={{ fontSize: 11, color: T.red, background: 'none', border: 'none', cursor: 'pointer', marginTop: 6 }}>Remove</button>
              </div>
            ))}
            <button onClick={() => setForm(f => ({ ...f, experience: [...f.experience, { company: '', role: '', duration: '', description: '' }] }))} style={{ ...smallBtnStyle, width: '100%' }}>+ Add Experience</button>
          </Field>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 20px', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10, flexShrink: 0 }}>
          <button onClick={onClose} style={{ ...smallBtnStyle, flex: 1 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: `linear-gradient(135deg,${T.cyan},${T.blue})`, color: '#07080c', fontSize: 13, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {saving ? <><Spinner/> Saving…</> : '💾 Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Resume card ───────────────────────────────────────────────────
function ResumeCard({ resume, onEdit, onDelete, onReparse, onSelect, selected }) {
  const [deleting,  setDeleting]  = useState(false);
  const [reparsing, setReparsing] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${resume.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    await onDelete(resume.id);
    setDeleting(false);
  };

  const handleReparse = async () => {
    setReparsing(true);
    await onReparse(resume.id);
    setReparsing(false);
  };

  return (
    <div style={{
      borderRadius: 12, border: `1.5px solid ${selected ? T.cyan + '60' : T.border}`,
      background: selected ? `${T.cyan}06` : T.bg2,
      padding: '16px', transition: 'all .2s',
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 18 }}>📄</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: T.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {resume.name}
            </span>
            {resume.ai_processed && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: `${T.green}15`, color: T.green, fontWeight: 700 }}>AI Parsed</span>}
          </div>
          <div style={{ fontSize: 12, color: T.muted }}>
            {resume.years_experience && `${resume.years_experience} years exp · `}
            {resume.skills?.length || 0} skills ·{' '}
            {new Date(resume.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* Skills preview */}
      {resume.skills?.length > 0 && (
        <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap' }}>
          {resume.skills.slice(0, 6).map((s, i) => <Chip key={i} label={s}/>)}
          {resume.skills.length > 6 && <Chip label={`+${resume.skills.length - 6} more`} color={T.muted}/>}
        </div>
      )}

      {/* Summary */}
      {resume.summary && (
        <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.6, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {resume.summary}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
        <button onClick={() => onSelect(resume)} style={{ ...actionBtnStyle, background: selected ? `${T.cyan}20` : T.bg3, color: selected ? T.cyan : T.muted, border: `1px solid ${selected ? T.cyan + '40' : T.border}` }}>
          {selected ? '✓ Selected' : 'Use in Tools'}
        </button>
        <button onClick={() => onEdit(resume)} style={actionBtnStyle}>✏️ Edit</button>
        <button onClick={handleReparse} disabled={reparsing} style={actionBtnStyle}>
          {reparsing ? '⟳ Parsing…' : '🤖 Re-parse'}
        </button>
        <button onClick={handleDelete} disabled={deleting} style={{ ...actionBtnStyle, color: T.red, borderColor: `${T.red}30` }}>
          {deleting ? '…' : '🗑 Delete'}
        </button>
      </div>
    </div>
  );
}

// Replace the list tab section in ResumeManager with this:

function ResumeList({ resumes, selected, onEdit, onDelete, onReparse, onSelect }) {
  const [index, setIndex] = useState(0);

  // Keep index in bounds if resumes change
  useEffect(() => {
    if (index >= resumes.length) setIndex(Math.max(0, resumes.length - 1));
  }, [resumes.length]);

  if (resumes.length === 0) return (
    <div style={{ textAlign:'center', padding:'40px 20px' }}>
      <div style={{ fontSize:40, marginBottom:12 }}>📄</div>
      <div style={{ fontSize:14, color:T.muted, marginBottom:16 }}>No resumes yet</div>
      <button onClick={() => setTab('upload')} style={{ padding:'9px 20px', borderRadius:8, border:'none', background:`linear-gradient(135deg,${T.cyan},${T.blue})`, color:'#07080c', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
        Upload your first resume →
      </button>
    </div>
  );

  const resume  = resumes[index];
  const isFirst = index === 0;
  const isLast  = index === resumes.length - 1;

  return (
    <div>
      {/* Navigation header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <button
          onClick={() => setIndex(i => i - 1)}
          disabled={isFirst}
          style={{
            width:32, height:32, borderRadius:'50%', border:`1px solid ${T.border}`,
            background: isFirst ? 'transparent' : T.bg3,
            color: isFirst ? T.dim : T.white,
            fontSize:16, cursor: isFirst ? 'default' : 'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            transition:'all .15s',
          }}
        >‹</button>

        {/* Dot indicators */}
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {resumes.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              style={{
                width:  i === index ? 20 : 7,
                height: 7,
                borderRadius: 4,
                border: 'none',
                background: i === index ? T.cyan : T.border,
                cursor: 'pointer',
                padding: 0,
                transition: 'all .2s',
              }}
            />
          ))}
        </div>

        <button
          onClick={() => setIndex(i => i + 1)}
          disabled={isLast}
          style={{
            width:32, height:32, borderRadius:'50%', border:`1px solid ${T.border}`,
            background: isLast ? 'transparent' : T.bg3,
            color: isLast ? T.dim : T.white,
            fontSize:16, cursor: isLast ? 'default' : 'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
            transition:'all .15s',
          }}
        >›</button>
      </div>

      {/* Counter */}
      <div style={{ textAlign:'center', fontSize:11.5, color:T.dim, marginBottom:12 }}>
        {index + 1} of {resumes.length}
      </div>

      {/* Single resume card */}
      <ResumeCard
        resume={resume}
        selected={selected?.id === resume.id}
        onEdit={onEdit}
        onDelete={(id) => {
          onDelete(id);
          // Move to previous if deleting last item
          if (index > 0 && index === resumes.length - 1) setIndex(i => i - 1);
        }}
        onReparse={onReparse}
        onSelect={onSelect}
      />
    </div>
  );
}
// ── Main component ────────────────────────────────────────────────
export default function ResumeManager({ onResumeSelect }) {
  const [resumes,   setResumes]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [selected,  setSelected]  = useState(null);
  const [error,     setError]     = useState('');
  const [tab,       setTab]       = useState('list'); // 'list' | 'upload'
  const limit = 5;

  useEffect(() => { fetchResumes(); }, []);

  const fetchResumes = async () => {
    try {
      const res  = await fetch(`${API}/api/resumes/`, { headers: authHeaders() });
      const data = await res.json();
      setResumes(data.resumes || []);
    } catch { setError('Failed to load resumes'); }
    finally  { setLoading(false); }
  };

  const handleUpload = async (formData) => {
    setUploading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/resumes/upload/`, {
        method:  'POST',
        headers: authHeaders(),
        body:    formData,
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Upload failed'); return false; }
      setResumes(prev => [data, ...prev]);
      setTab('list');
      return true;
    } catch { setError('Upload failed'); return false; }
    finally  { setUploading(false); }
  };

  const handleEdit = async (form) => {
    try {
      const res  = await fetch(`${API}/api/resumes/${editing.id}/update/`, {
        method:  'PATCH',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body:    JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Update failed'); return; }
      setResumes(prev => prev.map(r => r.id === editing.id ? data : r));
      if (selected?.id === editing.id) { setSelected(data); onResumeSelect?.(data); }
      setEditing(null);
    } catch { setError('Update failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${API}/api/resumes/${id}/delete/`, { method: 'DELETE', headers: authHeaders() });
      setResumes(prev => prev.filter(r => r.id !== id));
      if (selected?.id === id) { setSelected(null); onResumeSelect?.(null); }
    } catch { setError('Delete failed'); }
  };

  const handleReparse = async (id) => {
    try {
      const res  = await fetch(`${API}/api/resumes/${id}/reparse/`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Re-parse failed'); return; }
      setResumes(prev => prev.map(r => r.id === id ? data : r));
      if (selected?.id === id) { setSelected(data); onResumeSelect?.(data); }
    } catch { setError('Re-parse failed'); }
  };

  const handleSelect = (resume) => {
    const next = selected?.id === resume.id ? null : resume;
    setSelected(next);
    onResumeSelect?.(next);
  };

  return (
    <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif', color: T.white }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.white, marginBottom: 2 }}>📋 My Resumes</div>
          <div style={{ fontSize: 12, color: T.muted }}>{resumes.length}/{limit} saved · AI parses skills, experience & achievements</div>
        </div>
        <button
          onClick={() => setTab(tab === 'upload' ? 'list' : 'upload')}
          style={{
            padding: '7px 14px', borderRadius: 8, border: 'none',
            background: tab === 'upload' ? T.bg3 : `linear-gradient(135deg,${T.cyan},${T.blue})`,
            color: tab === 'upload' ? T.muted : '#07080c',
            fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {tab === 'upload' ? '← Back' : '+ Upload Resume'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: `${T.red}12`, border: `1px solid ${T.red}30`, fontSize: 13, color: T.red, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Upload tab */}
      {tab === 'upload' && (
        <UploadZone onUpload={handleUpload} uploading={uploading} count={resumes.length} limit={limit}/>
      )}

      {/* List tab */}
      {tab === 'list' && (
  <>
    {loading ? (
      <div style={{ textAlign:'center', padding:40, color:T.muted }}>
        <Spinner/> Loading resumes…
      </div>
    ) : (
      <ResumeList
        resumes={resumes}
        selected={selected}
        onEdit={setEditing}
        onDelete={handleDelete}
        onReparse={handleReparse}
        onSelect={handleSelect}
      />
    )}
  </>
)}

      {/* Edit modal */}
      {editing && (
        <EditModal resume={editing} onSave={handleEdit} onClose={() => setEditing(null)}/>
      )}
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 7,
  background: '#07080c', border: '1px solid rgba(255,255,255,.08)',
  color: '#dce6fa', fontSize: 13, fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box',
};
const smallBtnStyle = {
  padding: '7px 12px', borderRadius: 7, border: '1px solid rgba(255,255,255,.1)',
  background: 'rgba(255,255,255,.05)', color: 'rgba(220,230,250,.7)',
  fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
};
const actionBtnStyle = {
  padding: '5px 11px', borderRadius: 6,
  border: '1px solid rgba(255,255,255,.08)',
  background: '#111520', color: 'rgba(220,230,250,.6)',
  fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600,
};

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 11.5, fontWeight: 700, color: 'rgba(220,230,250,.55)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</label>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <span style={{
      width: 13, height: 13, borderRadius: '50%',
      border: '2px solid rgba(255,255,255,.15)', borderTopColor: '#00c8f0',
      animation: 'spin .65s linear infinite', display: 'inline-block',
    }}>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </span>
  );
}
